import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles } from 'lucide-react';
import { getWithExpiry, setWithExpiry } from '../utils/storage';

/**
 * SmartTooltip - Système de tooltips intelligents avec flèche pointant vers l'élément
 * 
 * Features:
 * - Bulle flottante stylée avec flèche
 * - Animation "effet wahou" (scale + glow)
 * - Position automatique (top/bottom/left/right selon l'espace)
 * - LocalStorage "ne plus afficher" par fonctionnalité
 * - Traductions FR/EN
 * 
 * Usage:
 * <SmartTooltip
 *   id="add-food"
 *   title={{ fr: "Ajouter un aliment", en: "Add food" }}
 *   description={{ fr: "Cliquez ici pour...", en: "Click here to..." }}
 *   targetRef={buttonRef}
 *   show={showTooltip}
 *   onDismiss={() => setShowTooltip(false)}
 *   en={false}
 * />
 */

export const SmartTooltip = ({
  id,
  title,
  description,
  targetRef,
  show,
  onDismiss,
  en = false,
  position = 'auto', // 'auto' | 'top' | 'bottom' | 'left' | 'right'
  delay = 500, // delay avant affichage (ms)
}) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0, arrow: 'bottom' });
  const tooltipRef = useRef(null);

  // Clic n'importe où hors de la bulle => dismiss (n'intercepte jamais les clics
  // destinés au reste de la page, contrairement à un backdrop plein écran).
  useEffect(() => {
    if (!visible) return;
    const onDocMouseDown = (e) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        handleDismiss(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown, true);
    return () => document.removeEventListener('mousedown', onDocMouseDown, true);
  }, [visible]);

  // Vérifier si l'utilisateur a déjà dismissé ce tooltip
  useEffect(() => {
    const dismissed = getWithExpiry(`bodyup_tooltip_${id}`);
    if (dismissed === '1') {
      return;
    }

    if (show && targetRef?.current) {
      const timer = setTimeout(() => {
        calculatePosition();
        setVisible(true);
      }, delay);
      // Re-position on scroll/resize AND periodically for the first ~2s to catch
      // late layout shifts (avatar image loading, sibling sections rendering).
      const onLayoutChange = () => calculatePosition();
      window.addEventListener('scroll', onLayoutChange, { passive: true, capture: true });
      window.addEventListener('resize', onLayoutChange);
      const settleInterval = setInterval(calculatePosition, 300);
      const settleTimeout = setTimeout(() => clearInterval(settleInterval), 2500);
      return () => {
        clearTimeout(timer);
        clearInterval(settleInterval);
        clearTimeout(settleTimeout);
        window.removeEventListener('scroll', onLayoutChange, { capture: true });
        window.removeEventListener('resize', onLayoutChange);
      };
    } else {
      setVisible(false);
    }
  }, [show, id, delay, targetRef]);

  const calculatePosition = () => {
    if (!targetRef?.current) return;

    const target = targetRef.current.getBoundingClientRect();
    const tooltipWidth = 320; // largeur estimée du tooltip
    const tooltipHeight = 200; // hauteur estimée
    const gap = 16; // espace entre target et tooltip
    const arrowSize = 12;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let x = 0, y = 0, arrowPos = 'bottom';

    // Calcul automatique de la meilleure position
    const forceTop = position === 'top';
    const forceBottom = position === 'bottom';
    const forceLeft = position === 'left';
    const forceRight = position === 'right';

    if (position === 'auto') {
      const spaceTop = target.top;
      const spaceBottom = viewportHeight - target.bottom;
      const spaceLeft = target.left;
      const spaceRight = viewportWidth - target.right;

      // Priorité : bottom > top > right > left
      if (spaceBottom >= tooltipHeight + gap) {
        x = target.left + target.width / 2 - tooltipWidth / 2;
        y = target.bottom + gap;
        arrowPos = 'top';
      } else if (spaceTop >= tooltipHeight + gap) {
        x = target.left + target.width / 2 - tooltipWidth / 2;
        y = target.top - tooltipHeight - gap;
        arrowPos = 'bottom';
      } else if (spaceRight >= tooltipWidth + gap) {
        x = target.right + gap;
        y = target.top + target.height / 2 - tooltipHeight / 2;
        arrowPos = 'left';
      } else {
        x = target.left - tooltipWidth - gap;
        y = target.top + target.height / 2 - tooltipHeight / 2;
        arrowPos = 'right';
      }
    } else if (forceTop) {
      x = target.left + target.width / 2 - tooltipWidth / 2;
      y = target.top - tooltipHeight - gap;
      arrowPos = 'bottom';
    } else if (forceBottom) {
      x = target.left + target.width / 2 - tooltipWidth / 2;
      y = target.bottom + gap;
      arrowPos = 'top';
    } else if (forceLeft) {
      x = target.left - tooltipWidth - gap;
      y = target.top + target.height / 2 - tooltipHeight / 2;
      arrowPos = 'right';
    } else if (forceRight) {
      x = target.right + gap;
      y = target.top + target.height / 2 - tooltipHeight / 2;
      arrowPos = 'left';
    }

    // Ajuster pour rester dans le viewport — sur mobile, on réserve aussi la zone de la
    // barre de navigation flottante en bas (~90px) pour ne jamais l'intercepter au clic.
    const bottomReserve = viewportWidth < 1024 ? 100 : 16;
    x = Math.max(16, Math.min(x, viewportWidth - tooltipWidth - 16));
    y = Math.max(16, Math.min(y, viewportHeight - tooltipHeight - bottomReserve));

    setCoords({ x, y, arrow: arrowPos });
  };

  const handleDismiss = (dontShowAgain = false) => {
    if (dontShowAgain) {
      setWithExpiry(`bodyup_tooltip_${id}`, '1');
    }
    setVisible(false);
    onDismiss?.();
  };

  // Si le tooltip fait partie d'un tour guidé, afficher la progression
  const showProgress = coords.arrow !== 'none' && typeof window !== 'undefined';

  const t = en ? title.en : title.fr;
  const d = en ? description.en : description.fr;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          ref={tooltipRef}
          initial={{ opacity: 0, scale: 0.8, y: 10 }}
          animate={{ 
            opacity: 1, 
            scale: 1, 
            y: 0,
            transition: { 
              type: 'spring', 
              stiffness: 400, 
              damping: 25,
              duration: 0.4 
            }
          }}
          exit={{ opacity: 0, scale: 0.9, y: 5, transition: { duration: 0.2 } }}
          style={{
            position: 'fixed',
            left: `${coords.x}px`,
            top: `${coords.y}px`,
            zIndex: 58,
          }}
          className="pointer-events-none"
        >
          {/* Tooltip Content */}
          <div className="relative w-80 max-w-[calc(100vw-32px)] pointer-events-none">
            {/* Glow Effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/30 via-pink-500/30 to-purple-500/30 blur-xl rounded-2xl pointer-events-none" />
            
            {/* Arrow */}
            {coords.arrow === 'top' && (
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-zinc-900 border-t border-l border-white/20 rotate-45" />
            )}
            {coords.arrow === 'bottom' && (
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-zinc-900 border-b border-r border-white/20 rotate-45" />
            )}
            {coords.arrow === 'left' && (
              <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-4 h-4 bg-zinc-900 border-l border-b border-white/20 rotate-45" />
            )}
            {coords.arrow === 'right' && (
              <div className="absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 bg-zinc-900 border-r border-t border-white/20 rotate-45" />
            )}

            {/* Main Card — seule zone qui intercepte réellement les clics : le conteneur
                fixed est pointer-events-none pour ne jamais bloquer les tuiles/boutons situés
                en dessous quand le calcul de position le fait chevaucher un autre élément. */}
            <motion.div
              onClick={(e) => e.stopPropagation()}
              initial={{ boxShadow: '0 0 0 rgba(255, 23, 68, 0)' }}
              animate={{ 
                boxShadow: [
                  '0 0 20px rgba(255, 23, 68, 0.3)',
                  '0 0 40px rgba(255, 23, 68, 0.5)',
                  '0 0 20px rgba(255, 23, 68, 0.3)',
                ],
              }}
              transition={{ duration: 2, repeat: Infinity }}
              className="relative bg-gradient-to-br from-zinc-900 to-zinc-950 border border-white/10 rounded-2xl p-5 shadow-2xl overflow-hidden pointer-events-auto"
            >
              {/* Animated Background Orbs */}
              <div className="absolute inset-0 opacity-30">
                <motion.div
                  animate={{ 
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.6, 0.3],
                  }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="absolute -top-10 -right-10 w-32 h-32 bg-primary/50 rounded-full blur-3xl"
                />
                <motion.div
                  animate={{ 
                    scale: [1, 1.3, 1],
                    opacity: [0.2, 0.5, 0.2],
                  }}
                  transition={{ duration: 4, repeat: Infinity, delay: 0.5 }}
                  className="absolute -bottom-10 -left-10 w-40 h-40 bg-pink-500/40 rounded-full blur-3xl"
                />
              </div>

              {/* Header */}
              <div className="relative flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <motion.div
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <Sparkles className="w-5 h-5 text-yellow-400" />
                  </motion.div>
                  <h4 className="font-bold text-white text-base">{t}</h4>
                </div>
                <button
                  onClick={() => handleDismiss(false)}
                  className="p-1.5 rounded-lg hover:bg-white/10 text-zinc-400 hover:text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Description */}
              <p className="relative text-sm text-zinc-300 leading-relaxed mb-4">
                {d}
              </p>

              {/* Actions */}
              <div className="relative flex items-center justify-between gap-3">
                <button
                  onClick={() => handleDismiss(true)}
                  className="text-xs text-zinc-400 hover:text-zinc-300 underline underline-offset-2"
                >
                  {en ? "Don't show again" : "Ne plus afficher"}
                </button>
                <button
                  onClick={() => handleDismiss(false)}
                  className="px-4 py-2 rounded-lg bg-gradient-to-r from-primary to-pink-500 text-white text-sm font-bold hover:scale-105 transition-transform"
                >
                  {en ? "Got it!" : "Compris !"}
                </button>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SmartTooltip;
